import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

/**
 * Sino de avisos da Setebit no cabeçalho do painel.
 *
 * Mostra o contador de não lidos e, no dropdown, os últimos comunicados. Ler o
 * texto completo é na tela /avisos — aqui é só o suficiente para a pessoa saber
 * que chegou algo e decidir abrir.
 *
 * Os dados vêm do hook useAvisos, montado uma vez no Layout: o sino e o modal do
 * aviso crítico compartilham a mesma chamada.
 */
const TEXTO = '#1f2328';

const formatarData = (valor) => {
	if (!valor) return '';
	// A API devolve "Y-m-d H:i:s"; o Safari não aceita esse formato com espaço.
	const data = new Date(valor.replace(' ', 'T'));
	return isNaN(data) ? '' : data.toLocaleString('pt-BR', {
		day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
	});
};

const SinoAvisos = ({ naoLidos = 0, ultimos = [], marcarTodosLidos }) => {
	const [aberto, setAberto] = useState(false);
	const containerRef = useRef(null);
	const history = useHistory();

	// Fecha ao clicar fora — sem isso o dropdown fica preso na tela.
	useEffect(() => {
		if (!aberto) return undefined;

		const aoClicarFora = (evento) => {
			if (containerRef.current && !containerRef.current.contains(evento.target)) {
				setAberto(false);
			}
		};

		document.addEventListener('mousedown', aoClicarFora);
		return () => document.removeEventListener('mousedown', aoClicarFora);
	}, [aberto]);

	const abrirTela = (id) => {
		setAberto(false);
		history.push(id ? `/avisos?aviso=${id}` : '/avisos');
	};

	return (
		<div ref={containerRef} style={styles.container}>
			<button
				type="button"
				onClick={() => setAberto(!aberto)}
				style={styles.botao}
				aria-label={naoLidos > 0 ? `${naoLidos} aviso(s) não lido(s)` : 'Avisos'}
				title="Avisos da Setebit"
			>
				<i className="fa fa-bell" style={styles.icone} />
				{naoLidos > 0 && (
					<span style={styles.badge}>{naoLidos > 99 ? '99+' : naoLidos}</span>
				)}
			</button>

			{aberto && (
				<div style={styles.dropdown}>
					<div style={styles.cabecalho}>
						<strong>Avisos</strong>
						{naoLidos > 0 && (
							<button type="button" style={styles.link} onClick={() => marcarTodosLidos()}>
								Marcar todos como lidos
							</button>
						)}
					</div>

					{ultimos.length === 0 ? (
						<p style={styles.vazio}>Nenhum aviso por aqui.</p>
					) : (
						<ul style={styles.lista}>
							{ultimos.map((aviso) => (
								<li
									key={aviso.id}
									style={{ ...styles.item, background: aviso.lido ? '#fff' : '#f7f9fc' }}
									onClick={() => abrirTela(aviso.id)}
								>
									<div style={styles.itemTopo}>
										<span style={{ ...styles.titulo, fontWeight: aviso.lido ? 400 : 600 }}>
											{aviso.titulo}
										</span>
										{aviso.critico && <span style={styles.tagCritico}>importante</span>}
									</div>
									<span style={styles.resumo}>{aviso.mensagem}</span>
									<span style={styles.data}>{formatarData(aviso.publicado_em)}</span>
								</li>
							))}
						</ul>
					)}

					<button type="button" style={styles.verTodos} onClick={() => abrirTela()}>
						Ver todos os avisos
					</button>
				</div>
			)}
		</div>
	);
};

const styles = {
	// float:right para conviver com o .profile_details do cabeçalho, que também flutua;
	// a altura casa com a do bloco do nome do usuário (.profile_img, 45px).
	container: {
		position: 'relative', float: 'right', height: 45, marginRight: 18,
		display: 'flex', alignItems: 'center',
	},
	botao: {
		background: 'none', border: 'none', padding: 4, cursor: 'pointer',
		position: 'relative', lineHeight: 1,
	},
	icone: { fontSize: 20, color: '#5f6368' },
	badge: {
		position: 'absolute', top: -4, right: -6, minWidth: 18, height: 18,
		padding: '0 5px', borderRadius: 9, background: '#d92d20', color: '#fff',
		fontSize: 11, lineHeight: '18px', fontWeight: 600, textAlign: 'center',
	},
	dropdown: {
		position: 'absolute', top: 34, right: 0, width: 340, zIndex: 1000,
		background: '#fff', border: '1px solid #e4e6eb', borderRadius: 8,
		boxShadow: '0 8px 24px rgba(16,24,40,.12)', color: TEXTO, overflow: 'hidden',
	},
	cabecalho: {
		display: 'flex', justifyContent: 'space-between', alignItems: 'center',
		padding: '10px 14px', borderBottom: '1px solid #eef0f3', fontSize: 14,
	},
	link: { background: 'none', border: 'none', padding: 0, color: '#1d6fbf', fontSize: 12, cursor: 'pointer' },
	lista: { listStyle: 'none', margin: 0, padding: 0, maxHeight: 320, overflowY: 'auto' },
	item: { padding: '10px 14px', borderBottom: '1px solid #f2f4f7', cursor: 'pointer' },
	itemTopo: { display: 'flex', alignItems: 'center', gap: 6 },
	titulo: { fontSize: 14 },
	tagCritico: {
		background: '#fce8ea', color: '#b32233', borderRadius: 4,
		fontSize: 10, padding: '1px 5px', textTransform: 'uppercase',
	},
	resumo: {
		display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
		overflow: 'hidden', fontSize: 12.5, color: '#667085', marginTop: 2,
	},
	data: { display: 'block', fontSize: 11, color: '#98a2b3', marginTop: 4 },
	vazio: { padding: '16px 14px', margin: 0, fontSize: 13, color: '#667085' },
	verTodos: {
		width: '100%', background: '#f9fafb', border: 'none', borderTop: '1px solid #eef0f3',
		padding: '10px 0', fontSize: 13, color: '#1d6fbf', cursor: 'pointer',
	},
};

export default SinoAvisos;
